import axios, { AxiosError } from 'axios';
import { logToGrayLog } from '@/actions/graylog';

export enum LOGGING_LEVEL {
  ERROR = 3,
  WARN = 4,
  INFO = 6,
  DEBUG = 7,
}

const GRAYLOG_ENABLED = process.env.GRAYLOG_ENABLED === 'true';
const LOG_LEVEL = LOGGING_LEVEL[process.env.LOG_LEVEL as unknown as keyof typeof LOGGING_LEVEL] || LOGGING_LEVEL.DEBUG;

const logToConsole = (message: string, level: LOGGING_LEVEL, additionalData?: object) => {
  const logMessage = `[${new Date().toISOString()}] [${LOGGING_LEVEL[level]}] ${message}`;
  const logData = additionalData ? JSON.stringify(additionalData, null, 2) : '';

  switch (level) {
    case LOGGING_LEVEL.ERROR:
      console.error(logMessage, logData);
      break;
    case LOGGING_LEVEL.WARN:
      console.warn(logMessage, logData);
      break;
    case LOGGING_LEVEL.INFO:
      console.info(logMessage, logData);
      break;
    case LOGGING_LEVEL.DEBUG:
      console.debug(logMessage, logData);
      break;
  }
};

const logMessage = (message: string, level: LOGGING_LEVEL, additionalData?: object, path?: string) => {
  if (level > LOG_LEVEL) return;
  if (GRAYLOG_ENABLED) {
    logToGrayLog(message, level, additionalData, path);
  }
  logToConsole(message, level, additionalData);
};

const logError = (message: string, error?: any) => {
  logMessage(message, LOGGING_LEVEL.ERROR, error, error?.url);
};

const logInfo = (message: string, additionalData?: object) => {
  logMessage(message, LOGGING_LEVEL.INFO, additionalData);
};

const logWarn = (message: string, additionalData?: object) => {
  logMessage(message, LOGGING_LEVEL.WARN, additionalData);
};

const logDebug = (message: string, additionalData?: object) => {
  logMessage(message, LOGGING_LEVEL.DEBUG, additionalData);
};

const sanitizeAxiosError = (error: AxiosError): Record<string, any> => ({
  message: error.message,
  code: error.code,
  url: error.config?.url,
  response: {
    status: error.response?.status,
    statusText: error.response?.statusText,
    data: error.response?.data,
  },
});

export const logSanitizedError = (error: any, message: string) => {
  const sanitizedError = axios.isAxiosError(error) ? sanitizeAxiosError(error) : { stack: error.stack };
  logError(message, sanitizedError);
};

const logger = {
  error: logError,
  info: logInfo,
  warn: logWarn,
  debug: logDebug,
};

export default logger;
